import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { Button, Popconfirm, Space } from 'antd'
import React, { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react'
import { ColDef, RowDragEndEvent, RowSelectionOptions, SelectionColumnDef } from 'ag-grid-enterprise'
import { Formula } from '../types/Formula'
import CodeBlock from '../../../components/Misc/CodeBlock'
import { useCtxFormulas } from '../queries/useCtxFormulas'
import { useUpdateFormulaOrder } from '../queries/useUpdateFormulaOrder'
import { useDeleteFormula } from '../queries/useDeleteFormula'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import { showErrorMessage } from '../../../utils/message'

interface FormulaTableProps {
  handleEdit: (formulas: Formula) => void
}

// Cell renderer for source/formula column
const SourceCellRenderer = ({ value }: CustomCellRendererProps<Formula, string>) => {
  if (!value) return null
  return <CodeBlock code={value.replace(/(\r\n|\n|\r)/gm, '')} />
}

// Cell renderer for action buttons
interface ActionCellRendererProps extends CustomCellRendererProps<Formula> {
  onEdit: (formula: Formula) => void
  onDelete: (id: number) => void
  deleteConfirmTitle: string
  yesText: string
  noText: string
}

const ActionCellRenderer = ({
  data,
  onEdit,
  onDelete,
  deleteConfirmTitle,
  yesText,
  noText
}: ActionCellRendererProps) => {
  if (!data) return null

  return (
    <Space>
      <Button size="small" type="text" icon={<EditOutlined />} onClick={() => onEdit(data)} />
      <Popconfirm
        placement="bottomRight"
        title={deleteConfirmTitle}
        onConfirm={() => onDelete(data.id)}
        okText={yesText}
        cancelText={noText}
      >
        <Button danger size="small" type="text" icon={<DeleteOutlined />} />
      </Popconfirm>
    </Space>
  )
}

const FormulaTable: React.FC<FormulaTableProps> = ({ handleEdit }) => {
  const { t } = useTranslation()
  const gridRef = useRef<AgGridReact<Formula>>(null)
  const { data: formulas = [], isLoading: formulasLoading, isFetching } = useCtxFormulas()
  const { mutateAsync: updateOrder, isPending: updateOrderPending } = useUpdateFormulaOrder()
  const { mutate: deleteFormula, isPending: deletePending } = useDeleteFormula()

  const loading = updateOrderPending || deletePending || formulasLoading || isFetching

  const defaultColDef = useMemo<ColDef<Formula>>(
    () => ({
      sortable: true,
      resizable: true,
      filter: false
    }),
    []
  )

  const columnDefs = useMemo<ColDef<Formula>[]>(
    () => [
      {
        field: 'name',
        headerName: t('global:name'),
        width: 250,
        rowDrag: true,
        filter: 'agTextColumnFilter',
        floatingFilter: true
      },
      {
        field: 'code',
        headerName: t('global:code'),
        width: 250,
        filter: 'agTextColumnFilter',
        floatingFilter: true
      },
      {
        field: 'source',
        headerName: t('global:formula'),
        flex: 1,
        floatingFilter: true,
        filter: 'agTextColumnFilter',
        cellRenderer: SourceCellRenderer
      },
      {
        headerName: t('global:actions'),
        width: 80,
        pinned: 'right',
        sortable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: ActionCellRenderer,
        cellRendererParams: {
          onEdit: handleEdit,
          onDelete: (id: number) => deleteFormula(id),
          deleteConfirmTitle: t('global:delete-confirm'),
          yesText: t('global:yes'),
          noText: t('global:no')
        }
      }
    ],
    [t, handleEdit, deleteFormula]
  )

  const rowSelection = useMemo<RowSelectionOptions | 'single' | 'multiple'>(() => {
    return { mode: 'multiRow', headerCheckbox: false }
  }, [])

  const selectionColumnDef = useMemo<SelectionColumnDef>(() => {
    return { width: 40 }
  }, [])

  const onRowDragEnd = useCallback(
    async ({ api }: RowDragEndEvent<Formula>) => {
      // Get the new order directly from the grid after managed drag
      const orderArray: Partial<Formula>[] = []
      api.forEachNode((node, index) => {
        if (node.data) {
          orderArray.push({
            id: +node.data.id,
            order: index
          })
        }
      })

      // Check if order actually changed
      const hasChanged = orderArray.some((item, index) => {
        const original = formulas[index]
        return original && original.id !== item.id
      })

      if (hasChanged) {
        try {
          await updateOrder(orderArray)
        } catch {
          showErrorMessage()
        }
      }
    },
    [formulas, updateOrder]
  )

  const getRowId = useCallback((params: { data: Formula }) => params.data.id.toString(), [])

  return (
    <CustomAgGrid
      ref={gridRef}
      domLayout="autoHeight"
      rowData={formulas}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      loading={loading}
      rowDragManaged
      onRowDragEnd={onRowDragEnd}
      getRowId={getRowId}
      rowDragMultiRow
      rowSelection={rowSelection}
      selectionColumnDef={selectionColumnDef}
    />
  )
}

export default FormulaTable
